import {
    Box,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    SxProps,
    TextField,
    Theme,
    Typography,
} from "@mui/material";
import * as React from "react";
import {useCallback, useEffect, useMemo} from "react";
import {AddOutlined, DeleteOutline, EditOutlined} from "@mui/icons-material";
import {ProjectType, Template} from "../../../../../../models/basic/template";
import {NpiProjectTypeService} from "../../../../../../services/npi-project-type-service";
import {ErrorComponent} from "../../../../../../components/error/error.component";
import {toast} from "react-toastify";
import {translate} from "../../../../../../translate/translate";
import GenericDialog from "../../../../../../components/dialog/generic-dialog.component";

interface CrudSelectProjectTypeProps {
    value: Template["project_type"] | ProjectType | "";
    onChange: (value: number | "") => void;
    error?: boolean;
    fullWidth?: boolean;
    formControlSx?: SxProps<Theme>;
    selectSx?: SxProps<Theme>;
    showOnlyAvailableTypes?: boolean;
}

const getProjectTypeValue = (projectType: CrudSelectProjectTypeProps["value"]) => {
    if (typeof projectType === "object" && projectType !== null && "id" in projectType) {
        return Number(projectType.id);
    }

    return projectType || "";
};

const CrudSelectProjectTypeComponent = (props: CrudSelectProjectTypeProps) => {
    const {
        value,
        onChange,
        error = false,
        fullWidth = true,
        formControlSx,
        selectSx,
        showOnlyAvailableTypes = false,
    } = props;

    const projectTypeService = useMemo(() => new NpiProjectTypeService(), []);
    const [projectTypeList, setProjectTypeList] = React.useState<ProjectType[]>([]);
    const [selectOpen, setSelectOpen] = React.useState(false);
    const [openProjectTypeDialog, setOpenProjectTypeDialog] = React.useState(false);
    const [projectTypeEditing, setProjectTypeEditing] = React.useState<ProjectType | undefined>();
    const [projectTypeName, setProjectTypeName] = React.useState("");

    const selectedValue = getProjectTypeValue(value);

    const loadProjectTypes = useCallback(() => {
        projectTypeService.readAll({ available: showOnlyAvailableTypes }).then(response => {
            setProjectTypeList(response.data);
        }).catch((error: any) => ErrorComponent(error));
    }, [projectTypeService]);

    useEffect(() => {
        loadProjectTypes();
    }, [loadProjectTypes]);

    const handleOpenProjectTypeDialog = (projectType?: ProjectType) => {
        setSelectOpen(false);
        setProjectTypeEditing(projectType);
        setProjectTypeName(projectType?.name ?? "");
        setOpenProjectTypeDialog(true);
    };

    const handleCloseProjectTypeDialog = () => {
        setOpenProjectTypeDialog(false);
        setProjectTypeEditing(undefined);
        setProjectTypeName("");
    };

    const handleSubmitProjectType = () => {
        const name = projectTypeName.trim();

        if (!name) {
            return;
        }

        const request = projectTypeEditing?.id
            ? projectTypeService.patch(projectTypeEditing.id, {name})
            : projectTypeService.create({name} as ProjectType);

        request.then((response) => {
            toast.success(translate.t(projectTypeEditing?.id ? "successfully_updated" : "successfully_registered"));
            loadProjectTypes();

            if (!projectTypeEditing?.id && response.data.id) {
                onChange(response.data.id);
            }

            handleCloseProjectTypeDialog();
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    };

    const handleDeleteProjectType = (projectType: ProjectType) => {
        setSelectOpen(false);

        projectTypeService.delete(projectType.id!!).then(() => {
            toast.success(translate.t("successfully_deleted"));
            loadProjectTypes();

            if (selectedValue === projectType.id) {
                onChange("");
            }
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    };

    return (
        <Box sx={selectSx}>
            <FormControl fullWidth={fullWidth} required error={error} sx={formControlSx}>
                <InputLabel>{translate.t("project_type")}</InputLabel>
                <Select
                    label={`${translate.t("project_type")}"*"}`}
                    value={selectedValue}
                    open={selectOpen}
                    sx={{
                        maxWidth: "320px",
                        "& .MuiTypography-root": {
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap"
                        }
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 300,
                                maxWidth: 320
                            },
                        },
                    }}
                    onOpen={() => setSelectOpen(true)}
                    onClose={() => setSelectOpen(false)}
                    onChange={(event) => {
                        const eventValue = event.target.value as number | string;
                        if (eventValue === "create_project_type") {
                            setSelectOpen(false);
                            handleOpenProjectTypeDialog();
                            return;
                        }
                        onChange(eventValue as number);
                    }}>
                    <MenuItem value={"create_project_type"} sx={{maxWidth: "320px"}}>
                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%"}}>
                            <Typography color="primary">{translate.t("create_new")}</Typography>
                            <Box sx={{display: "initial", textAlign: "right"}}>
                                <IconButton size="small">
                                    <AddOutlined fontSize="small"/>
                                </IconButton>
                            </Box>
                        </Box>
                    </MenuItem>
                    {projectTypeList.map((item) => (
                        <MenuItem key={item.id} value={item.id} sx={{maxWidth: "320px"}}>
                            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 1}}>
                                <Typography sx={{
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    width: "80%"
                                }}>{item.name}</Typography>
                                <Box sx={{display: "initial", textAlign: "right", width: "20%"}}>
                                    <IconButton
                                        size="small"
                                        onMouseDown={(event) => event.stopPropagation()}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleOpenProjectTypeDialog(item);
                                        }}>
                                        <EditOutlined fontSize="small"/>
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        disabled={item.in_use}
                                        onMouseDown={(event) => event.stopPropagation()}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleDeleteProjectType(item);
                                        }}>
                                        <DeleteOutline fontSize="small"/>
                                    </IconButton>
                                </Box>
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <GenericDialog
                open={openProjectTypeDialog}
                onClose={handleCloseProjectTypeDialog}
                title={projectTypeEditing?.id ? "edit_project_type" : "new_project_type"}
                colorButton={"primary"}
                labelButton={"save"}
                disableConfirm={!projectTypeName.trim()}
                handleConfirm={handleSubmitProjectType}>
                <Box>
                    <TextField
                        label={translate.t("project_type")}
                        variant={"outlined"}
                        fullWidth
                        required
                        autoFocus
                        value={projectTypeName}
                        onChange={(event) => {
                            const newValue = event.target.value;
                            if (newValue.length > 256) return;
                            setProjectTypeName(newValue);
                        }}
                        inputProps={{maxLength: 256}}
                    />
                </Box>
            </GenericDialog>
        </Box>
    )
}

export default CrudSelectProjectTypeComponent;
